import { useNavigate } from "react-router-dom";
import { Zap, Sparkles, Star, Users, MousePointerClick, ArrowRight, Play, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useHomepageStats, formatStatNumber } from "@/hooks/useHomepageStats";
import heroMockupEn from "@/assets/hero-mockup-en.webp";
import heroMockupEs from "@/assets/hero-mockup-es.webp";

// IT mockup served from public/ for instant LCP discovery via <link rel="preload">
const heroMockups: Record<string, string> = {
  it: "/hero-mockup.webp",
  en: heroMockupEn,
  es: heroMockupEs,
};

const floatingOrb = (delay: number, x: string, y: string, size: string) => (
  <div
    className={`absolute ${x} ${y} ${size} rounded-full bg-accent/20 blur-3xl will-change-transform animate-pulse-slow`}
    style={{ animationDelay: `${delay}s` }}
  />
);

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { data: stats } = useHomepageStats();
  const handleCta = () => navigate(user ? "/dashboard" : "/auth?tab=register");

  const heroMockupSrc = heroMockups[i18n.language] || "/hero-mockup.webp";

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-background to-secondary/30 pointer-events-none" />
      {floatingOrb(0, "left-[10%]", "top-[20%]", "w-72 h-72")}
      {floatingOrb(2, "right-[5%]", "top-[10%]", "w-96 h-96")}
      {floatingOrb(4, "left-[40%]", "bottom-[0%]", "w-64 h-64")}

      <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-20">
        <div className="flex-1 max-w-xl animate-fade-in-up order-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-6 border border-accent/20 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Sparkles className="w-3.5 h-3.5" />
            {t("hero.badge")}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-foreground">
            {t("hero.title1")}
            <span className="text-gradient-accent">{t("hero.titleHighlight")}</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }} dangerouslySetInnerHTML={{ __html: t("hero.desc") }} />
          <div className="mt-8 flex flex-col items-start gap-3 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <Button
              size="lg"
              className="group flex-col gap-0 text-base sm:text-lg font-bold px-8 py-4 h-auto min-h-[64px] rounded-2xl bg-gradient-accent hover:opacity-90 transition-all text-accent-foreground border-0 shadow-elevated hover:scale-[1.02] w-full sm:w-auto sm:min-w-[320px]"
              onClick={handleCta}
            >
              <span className="inline-flex items-center gap-2">
                <Zap className="w-5 h-5" />
                {t("hero.cta")}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </span>
              <span className="text-xs font-medium opacity-90 mt-0.5">{t("hero.ctaHint")}</span>
            </Button>
            <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" />
              {t("hero.subCta")}
            </span>
          </div>

          {/* Stats row — real social proof above the fold */}
          <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4 animate-fade-in-up" style={{ animationDelay: '0.75s' }}>
            <div className="flex flex-col p-3 sm:p-4 rounded-2xl bg-card border border-border/60 shadow-sm">
              <div className="flex items-center gap-1.5 text-foreground">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">{formatStatNumber(stats?.creators ?? 600)}</span>
              </div>
              <span className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">{t("hero.statCreators")}</span>
            </div>
            <div className="flex flex-col p-3 sm:p-4 rounded-2xl bg-card border border-border/60 shadow-sm">
              <div className="flex items-center gap-1.5 text-foreground">
                <MousePointerClick className="w-4 h-4 text-accent" />
                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">{formatStatNumber(stats?.clicks ?? 700)}</span>
              </div>
              <span className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">{t("hero.statClicks")}</span>
            </div>
            <div className="flex flex-col p-3 sm:p-4 rounded-2xl bg-card border border-border/60 shadow-sm">
              <div className="flex items-center gap-1.5 text-foreground">
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">{(stats?.rating ?? 4.9).toFixed(1)}</span>
              </div>
              <span className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">{t("hero.statRating")}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex justify-center relative order-2 w-full">
          <div className="absolute inset-0 m-auto w-3/4 h-3/4 bg-accent/15 rounded-full blur-[80px]" />

          <div className="relative w-full max-w-xs sm:max-w-md">
            {/* Demo badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground text-background text-xs font-semibold shadow-elevated">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" />
              </span>
              {t("hero.demoBadge")}
            </div>

            <img
              src={heroMockupSrc}
              alt={t("hero.imgAlt")}
              className="relative w-full rounded-3xl shadow-elevated will-change-transform animate-float"
              fetchPriority="high"
              decoding="async"
              width={448}
              height={560}
            />

            {/* 10s loop demo overlay: click pulse + sale toast */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Click pulse */}
              <div className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 hero-demo-click">
                <div className="relative w-14 h-14 rounded-full bg-accent/30 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-2 border-accent hero-demo-ring" />
                  <Play className="w-5 h-5 text-accent fill-accent" />
                </div>
              </div>

              {/* Sale toast */}
              <div className="absolute top-6 -right-2 sm:-right-6 hero-demo-toast">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border shadow-elevated">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-xs font-bold text-foreground">{t("hero.demoSaleLabel")}</span>
                    <span className="text-[10px] text-muted-foreground">{t("hero.demoLinkLabel")}</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-muted-foreground">{t("hero.demoCaption")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
